/**
 * One package, and everything hanging off it.
 *
 * The screen a coordinator lives in, rebuilt against the approved prototype: a sticky summary
 * sidebar beside a register workspace. The sidebar is not decoration — it is the context that
 * makes an edit safe. Someone changing a certified amount needs to see the budget, the slip
 * and which gate the package is at without leaving the row they are editing.
 *
 * The tab strip carries a live count per register. A tab that says "Risks 12" tells someone
 * whether to click it; a tab that just says "Risks" makes them click all eight to find out
 * where the work actually is.
 *
 * The register tab is part of the address, so "look at the risks on this package" is a link
 * someone can paste rather than a set of instructions.
 */
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api, ApiError } from '@/api/client'
import type { PackageDetail, RegisterInfo, RegisterRow } from '@/api/types'
import { navigate } from '@/app/routes'
import { useViewPeriod } from '@/app/viewPeriod'
import { Icon } from '@/components/Icon'
import { Failed, Skeleton } from '@/components/states'
import { RiskMatrix } from '@/features/packages/RiskMatrix'
import { StatusTimeline } from '@/features/packages/StatusTimeline'
import { ConfirmEdit } from '@/features/registers/ConfirmEdit'
import { RegisterGrid, type PendingEdit } from '@/features/registers/RegisterGrid'
import { formatDate, formatMoney, formatPercent } from '@/lib/format'
import { registerLabel } from '@/lib/registers'

export function PackageWorkspace({ packageId, tab }: { packageId: string; tab: string }) {
  const { t, i18n } = useTranslation()
  const [pending, setPending] = useState<PendingEdit | null>(null)
  const [raised, setRaised] = useState<string | null>(null)
  const [unlocking, setUnlocking] = useState<RegisterRow | null>(null)
  const viewPeriod = useViewPeriod((state) => state.code)

  const detail = useQuery({
    queryKey: ['package', packageId, viewPeriod],
    queryFn: ({ signal }) =>
      api.get<PackageDetail>(
        `/packages/${packageId}${viewPeriod ? `?period=${viewPeriod}` : ''}`,
        signal,
      ),
  })

  const catalogue = useQuery({
    queryKey: ['registers'],
    queryFn: ({ signal }) => api.get<RegisterInfo[]>('/registers', signal),
    // Derived from the schema. It changes on deploy, not during a session.
    staleTime: Infinity,
  })

  if (detail.isError) {
    const error = detail.error
    return (
      <main className="page">
        <Failed
          message={error instanceof ApiError ? error.friendly : String(error)}
          onRetry={() => void detail.refetch()}
        />
      </main>
    )
  }

  if (detail.isPending || catalogue.isPending) {
    return (
      <main className="page" data-testid="package">
        <Skeleton rows={6} />
      </main>
    )
  }

  const pkg = detail.data
  const registers = catalogue.data ?? []
  const active = registers.find((register) => register.slug === tab) ?? null

  return (
    <main className="page page--workspace" data-testid="package">
      <nav className="crumbs" aria-label={t('a11y.breadcrumb')}>
        <button
          type="button"
          className="crumbs__link"
          onClick={() => navigate({ name: 'projects' })}
        >
          {t('nav.projects')}
        </button>
        <Icon name="chevronEnd" size={13} strokeWidth={2} className="crumbs__sep" />
        <button
          type="button"
          className="crumbs__link numeric"
          onClick={() => navigate({ name: 'project', projectId: pkg.project_id })}
        >
          {pkg.project_number ?? t('package.project')}
        </button>
        <Icon name="chevronEnd" size={13} strokeWidth={2} className="crumbs__sep" />
        <span className="crumbs__here numeric">{pkg.manual_package_id}</span>
        {active ? (
          <>
            <Icon name="chevronEnd" size={13} strokeWidth={2} className="crumbs__sep" />
            <span className="crumbs__tab">{registerLabel(t, active.slug, active.label)}</span>
          </>
        ) : null}
      </nav>

      <div className="workspace">
        <aside className="workspace__side">
          <section className="card card--summary" data-testid="package-summary">
            <header className="summary__head">
              <p className="summary__id numeric">{pkg.manual_package_id}</p>
              <p className="summary__name">{pkg.package_name ?? pkg.contract_title ?? '—'}</p>
            </header>

            <div className="summary__chips">
              <span className={`chip chip--${pkg.status}`}>
                {t(`status.${pkg.status}`, pkg.status)}
              </span>
              {pkg.severe_risks > 0 ? (
                <span className="chip chip--bad">
                  <Icon name="alert" size={11} strokeWidth={2} />
                  {t('package.severeRisks', { count: pkg.severe_risks })}
                </span>
              ) : null}
            </div>

            <Progress
              planned={pkg.planned_pct}
              actual={pkg.actual_pct}
              locale={i18n.language}
            />

            <dl className="facts">
              <Fact
                label={t('package.budget')}
                value={formatMoney(pkg.package_budget, i18n.language)}
              />
              <Fact
                label={t('package.slip')}
                value={
                  pkg.slip_days === null
                    ? '—'
                    : t('package.days', { count: Math.abs(pkg.slip_days) })
                }
                tone={pkg.slip_days !== null && pkg.slip_days > 0 ? 'bad' : undefined}
              />
              <Fact
                label={t('package.gate')}
                // No spaces around the slash. With them, "5 / 6" is two separate number runs
                // divided by neutral whitespace, and in an Arabic paragraph the neutrals take
                // the paragraph direction and the whole thing renders "6 / 5". Closed up, the
                // slash is a common separator between digits and the pair stays a unit.
                value={pkg.gate_no === null ? '—' : `${pkg.gate_no}/${pkg.gate_count}`}
              />
              <Fact label={t('package.district')} value={pkg.labels['district_id'] ?? '—'} />
            </dl>

            {pkg.progress_month ? (
              <p className="summary__asAt">
                {t('package.asAt', { month: formatDate(pkg.progress_month, i18n.language) })}
              </p>
            ) : null}

            <div className="summary__contract">
              <p className="summary__caption">{t('package.contract')}</p>
              <p className="summary__contractor">{pkg.labels['contractor_id'] ?? '—'}</p>
              <p className="summary__pm">
                {pkg.lead_project_manager ?? '—'} · {t('package.manager')}
              </p>
            </div>
          </section>

          <StatusTimeline packageId={packageId} />
        </aside>

        <div className="workspace__main">
          {raised ? (
            <p className="banner banner--good" role="status" data-testid="raised-banner">
              <Icon name="check" size={15} strokeWidth={2.4} />
              <span className="banner__text">{t('edit.raised', { number: raised })}</span>
              <button
                type="button"
                className="banner__action"
                onClick={() => navigate({ name: 'inbox' })}
              >
                {t('edit.viewInbox')}
              </button>
            </p>
          ) : null}

          {unlocking ? (
            <p className="banner banner--locked" role="status" data-testid="unlock-banner">
              <Icon name="lock" size={15} strokeWidth={1.8} />
              <span className="banner__text">
                {t('grid.lockedExplain', {
                  reference: String(unlocking['register_number'] ?? ''),
                })}
              </span>
              <button
                type="button"
                className="banner__dismiss"
                onClick={() => setUnlocking(null)}
                aria-label={t('a11y.dismiss')}
              >
                <Icon name="close" size={14} strokeWidth={2.2} />
              </button>
            </p>
          ) : null}

          <nav className="tabs" aria-label={t('package.registers')} data-scroll="1">
            {registers.map((register) => (
              <button
                key={register.slug}
                type="button"
                className="tabs__tab"
                // The slug, so the tab can wear the register's colour. Same attribute the
                // rail dot uses; the colour is keyed to the register, never to a position.
                data-register={register.slug}
                aria-current={tab === register.slug ? 'page' : undefined}
                onClick={() => {
                  setPending(null)
                  setRaised(null)
                  navigate({ name: 'package', packageId, tab: register.slug })
                }}
                data-testid={`tab-${register.slug}`}
              >
                <span className="tabs__dot" aria-hidden="true" />
                {registerLabel(t, register.slug, register.label)}
                <span className="tabs__count numeric">
                  {pkg.register_counts[register.slug] ?? 0}
                </span>
              </button>
            ))}
          </nav>

          {active?.slug === 'risks' ? <RiskMatrix packageId={packageId} /> : null}

          {active ? (
            <RegisterGrid
              register={active}
              packageId={packageId}
              onPropose={(edit) => {
                setRaised(null)
                setPending(edit)
              }}
              onUnlock={(row) => setUnlocking(row)}
            />
          ) : (
            <Overview pkg={pkg} registers={registers} />
          )}
        </div>
      </div>

      {pending ? (
        <ConfirmEdit
          register={pending.register}
          row={pending.row}
          changes={pending.changes}
          packageId={packageId}
          onCancel={() => setPending(null)}
          onRaised={(number) => {
            setPending(null)
            setRaised(number)
          }}
        />
      ) : null}
    </main>
  )
}

function Overview({ pkg, registers }: { pkg: PackageDetail; registers: RegisterInfo[] }) {
  const { t, i18n } = useTranslation()
  const total = registers.reduce(
    (sum, register) => sum + (pkg.register_counts[register.slug] ?? 0),
    0,
  )

  return (
    <section className="card card--pad" data-testid="package-overview">
      <h2 className="card__title">{t('package.summary')}</h2>
      <dl className="details">
        <Detail label={t('package.contractTitle')} value={pkg.contract_title} />
        <Detail
          label={t('package.phase')}
          value={pkg.labels['delivery_phase_id'] ?? pkg.phase_code}
        />
        <Detail label={t('package.assetType')} value={pkg.labels['asset_type_id'] ?? null} />
        <Detail
          label={t('package.plannedStart')}
          value={formatDate(pkg.planned_start, i18n.language)}
        />
        <Detail
          label={t('package.plannedFinish')}
          value={formatDate(pkg.planned_finish, i18n.language)}
        />
        <Detail
          label={t('package.location')}
          value={
            pkg.latitude && pkg.longitude
              ? `${Number(pkg.latitude).toFixed(4)}, ${Number(pkg.longitude).toFixed(4)}`
              : // Not a blank: nine of 517 real packages have no coordinates and someone has
                // to chase each one. Saying so is the point.
                t('package.noLocation')
          }
        />
      </dl>
      <p className="card__note">{t('package.registerTotal', { count: total })}</p>
    </section>
  )
}

/**
 * Planned against actual, as one bar and one sentence.
 *
 * This replaced two rows of a definition list. "Planned 62.4%" above "Actual 54.8%" is
 * accurate and asks the reader to subtract: the number that decides whether anyone needs to
 * do something about this package was not on the card, only the two numbers it comes from.
 *
 * The bar is two fills in one track rather than two bars, because the question is not "how
 * far along are we" twice — it is where actual sits relative to plan, and overlaying them
 * makes the gap the thing you see.
 *
 * The gap is stated in *points*, not as a percentage: 54.8% against 62.4% is 7.6 points
 * behind, and calling that "12% behind" — which is the other true statement — is how a
 * status report ends up in an argument.
 */
function Progress({
  planned,
  actual,
  locale,
}: {
  planned: string | number | null
  actual: string | number | null
  locale: string
}) {
  const { t } = useTranslation()

  // A package with no progress reported yet is a real state, and a bar drawn at zero would
  // claim it had made none.
  if (planned === null || actual === null) return null

  const plannedPct = Number(planned)
  const actualPct = Number(actual)
  if (!Number.isFinite(plannedPct) || !Number.isFinite(actualPct)) return null

  const gap = actualPct - plannedPct
  // A tenth of a point is inside the noise of monthly percentage reporting, so it is level.
  const state = gap <= -0.05 ? 'behind' : gap >= 0.05 ? 'ahead' : 'level'
  const points = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Math.abs(gap))

  return (
    <div className="progress" data-state={state} data-testid="package-progress">
      <div className="progress__legend">
        <span className="progress__actual numeric">
          {t('package.actual')} {formatPercent(actual, locale)}
        </span>
        <span className="progress__planned numeric">
          {t('package.planned')} {formatPercent(planned, locale)}
        </span>
      </div>

      {/* The track is decoration; the sentence below carries the meaning, so the bar is
          hidden from the accessibility tree rather than described twice. */}
      <div className="progress__track" aria-hidden="true">
        <span
          className="progress__fill progress__fill--planned"
          style={{ inlineSize: `${clamp(plannedPct)}%` }}
        />
        <span
          className="progress__fill progress__fill--actual"
          style={{ inlineSize: `${clamp(actualPct)}%` }}
        />
      </div>

      <p className="progress__gap">
        <Icon
          name={state === 'behind' ? 'arrowDown' : state === 'ahead' ? 'arrowUp' : 'level'}
          size={13}
          strokeWidth={2.8}
          className="progress__arrow"
        />
        {state === 'level'
          ? t('package.onPlan')
          : state === 'behind'
            ? t('package.behindPlan', { points })
            : t('package.aheadPlan', { points })}
      </p>
    </div>
  )
}

/** Progress is reported by hand and occasionally arrives at 104%. The bar stays in its
 *  track; the figure beside it still says 104%, because that is what was reported. */
function clamp(value: number): number {
  return Math.min(100, Math.max(0, value))
}

function Fact({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: string | undefined
}) {
  return (
    <>
      <dt className="facts__label">{label}</dt>
      <dd className="facts__value numeric" data-tone={tone}>
        {value}
      </dd>
    </>
  )
}

function Detail({ label, value }: { label: string; value: string | null }) {
  // The pair is wrapped, which HTML5 allows inside a <dl>, so the two can be laid out as a
  // row rather than as two independent grid items. Without it the label and the value are
  // siblings among eighteen siblings and no layout can keep them together.
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value ?? '—'}</dd>
    </div>
  )
}
